import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';

const ROLES = [
  { value: '',                    label: 'Select your role…' },
  { value: 'chartering_manager',  label: 'Chartering Manager' },
  { value: 'operations_director', label: 'Operations Director' },
  { value: 'fleet_manager',       label: 'Fleet Manager' },
  { value: 'ship_manager',        label: 'Ship Manager' },
  { value: 'trader',              label: 'Trader' },
  { value: 'broker',              label: 'Broker' },
  { value: 'other',               label: 'Other' },
];

function Field({ label, error, children }) {
  return (
    <div>
      <label style={{
        display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.7px',
        fontWeight: 600, color: '#8892a5', marginBottom: 6,
      }}>
        {label}
      </label>
      {children}
      {error && (
        <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{error}</div>
      )}
    </div>
  );
}

function inputStyle(focused, hasError) {
  return {
    width: '100%', padding: '10px 14px',
    border: `1px solid ${hasError ? 'rgba(239,68,68,0.5)' : focused ? '#e9a020' : '#1e2d44'}`,
    borderRadius: 8,
    background: 'rgba(10,22,40,0.7)',
    color: '#fff',
    fontFamily: "'Figtree', sans-serif", fontSize: 14,
    outline: 'none', boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 2px rgba(233,160,32,0.18)' : 'none',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };
}

export default function ProWaitlistModal({ onClose, interestSource = 'general' }) {
  const { user } = useUser();

  const [form, setForm] = useState({
    name:    user?.fullName || '',
    email:   user?.primaryEmailAddress?.emailAddress || '',
    company: '',
    role:    '',
  });
  const [errors,    setErrors]    = useState({});
  const [focused,   setFocused]   = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting,setSubmitting] = useState(false);

  const set = (key, val) => {
    setForm(f => ({ ...f, [key]: val }));
    if (errors[key]) setErrors(e => ({ ...e, [key]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required';
    if (!form.email.trim())   e.email   = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.company.trim()) e.company = 'Company is required';
    if (!form.role)           e.role    = 'Please select a role';
    return e;
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);

    try {
      window.__posthog?.capture('pro_waitlist_joined', {
        company:         form.company,
        role:            form.role,
        source_module:   'bunker_prices',
        interest_source: interestSource,
      });
      window.__posthog?.people?.set({
        waitlist_joined:  true,
        waitlist_date:    new Date().toISOString(),
        waitlist_company: form.company,
        waitlist_role:    form.role,
      });
    } catch (_) { /* posthog unavailable */ }

    await new Promise(r => setTimeout(r, 600));
    setSubmitted(true);
    setSubmitting(false);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 1000,
        }}
      />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 1001, width: 'min(480px, 92vw)',
        background: 'rgba(17,26,46,0.92)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(233,160,32,0.20)',
        borderRadius: 14,
        padding: 32,
        boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(233,160,32,0.08) inset',
      }}>
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 16, right: 16, background: 'none',
            border: 'none', color: '#8892a5', fontSize: 20, cursor: 'pointer',
            lineHeight: 1, padding: '4px 8px',
          }}
          aria-label="Close"
        >
          ×
        </button>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 10 }}>
              You're on the list!
            </div>
            <div style={{ fontSize: 14, color: '#8892a5', lineHeight: 1.6 }}>
              We'll notify you when expanded port coverage goes live.
              Early adopters lock in at <strong style={{ color: '#e9a020' }}>$49/month</strong>.
            </div>
            <button
              onClick={onClose}
              style={{
                marginTop: 24, padding: '10px 28px',
                background: '#e9a020', color: '#0a1628',
                border: 'none', borderRadius: 8,
                fontFamily: "'Fraunces', serif", fontSize: 14, fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 19, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
              Get Early Access to FathomVoyage Pro
            </div>
            <div style={{ fontSize: 13, color: '#8892a5', marginBottom: 24, lineHeight: 1.6 }}>
              Join the waitlist and lock in early-adopter pricing at{' '}
              <strong style={{ color: '#e9a020' }}>$49/month</strong>{' '}
              (regular $79). We'll notify you when expanded port coverage goes live.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Field label="Name" error={errors.name}>
                <input
                  style={inputStyle(focused === 'name', !!errors.name)}
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  onFocus={() => setFocused('name')}
                  onBlur={() => setFocused(null)}
                  placeholder="Your name"
                />
              </Field>

              <Field label="Work Email" error={errors.email}>
                <input
                  type="email"
                  style={inputStyle(focused === 'email', !!errors.email)}
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  onFocus={() => setFocused('email')}
                  onBlur={() => setFocused(null)}
                  placeholder="you@company.com"
                />
              </Field>

              <Field label="Company" error={errors.company}>
                <input
                  style={inputStyle(focused === 'company', !!errors.company)}
                  value={form.company}
                  onChange={e => set('company', e.target.value)}
                  onFocus={() => setFocused('company')}
                  onBlur={() => setFocused(null)}
                  placeholder="Your company"
                />
              </Field>

              <Field label="Role" error={errors.role}>
                <select
                  value={form.role}
                  onChange={e => set('role', e.target.value)}
                  onFocus={() => setFocused('role')}
                  onBlur={() => setFocused(null)}
                  style={{
                    ...inputStyle(focused === 'role', !!errors.role),
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' viewBox='0 0 10 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1L5 5L9 1' stroke='%238892a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', paddingRight: 36,
                  }}
                >
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%', marginTop: 24, padding: '12px 0',
                background: submitting ? 'rgba(233,160,32,0.50)' : '#e9a020',
                color: '#0a1628',
                border: 'none',
                borderRadius: 8, fontFamily: "'Fraunces', serif", fontSize: 15,
                fontWeight: 700, cursor: submitting ? 'wait' : 'pointer', transition: 'opacity 0.15s',
                opacity: submitting ? 0.7 : 1,
              }}
            >
              {submitting ? 'Joining…' : 'Join the Waitlist — Early Access at $49/mo →'}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
